import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus, Trash2 } from "lucide-react";
import ChordBeatCarousel from "@/components/music/ChordBeatCarousel";
import ChordGridComposer from "@/components/music/ChordGridComposer";
import AdminChordSelect from "./AdminChordSelect";
import {
  buildBeatGrid,
  getActiveChordAtTime,
  getChordChangeAtBeat,
  getChordLabel,
  getNextChordChange,
  normalizeChordChanges,
  normalizeChordEvent,
} from "@/music/chords/chordSyncUtils";

const SECTION_OPTIONS = ["Intro", "Verse", "Chorus", "Bridge", "Outro"];
const TIMING_OPTIONS = ["manual", "real", "estimated"];

const AdminChordTimelineEditor = ({
  chords = [],
  onChange,
  duration = 180,
  bpm = 120,
  musicalKey = "",
  currentTime = 0,
  isPlaying = false,
  onSeek,
  onTogglePlayback,
}) => {
  const [selectedBeatIndex, setSelectedBeatIndex] = useState(0);
  const [selectedChangeId, setSelectedChangeId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [focusSelectorToken, setFocusSelectorToken] = useState(0);
  const [viewMode, setViewMode] = useState("rail");
  const wasPlayingRef = useRef(false);
  const changes = useMemo(() => normalizeChordChanges(chords, duration), [chords, duration]);
  const beats = useMemo(() => buildBeatGrid({ duration, bpm }), [bpm, duration]);
  const selectedBeat = beats[selectedBeatIndex] || beats[0];
  const selectedChange = changes.find((change) => change.id === selectedChangeId)
    || getChordChangeAtBeat(changes, selectedBeat);
  const activeChange = getActiveChordAtTime(changes, currentTime);
  const nextChange = getNextChordChange(changes, currentTime);
  const activeBeatIndex = beats.findIndex((beat) => currentTime >= beat.start && currentTime < beat.end);
  const recentChords = useMemo(
    () => [...new Set(changes.slice(-8).map((change) => getChordLabel(change)))],
    [changes]
  );

  useEffect(() => {
    if (wasPlayingRef.current && !isPlaying && activeBeatIndex >= 0) {
      setSelectedBeatIndex(activeBeatIndex);
      setSelectedChangeId(activeChange?.id || null);
    }
    wasPlayingRef.current = isPlaying;
  }, [activeBeatIndex, activeChange, isPlaying]);

  const commit = useCallback((nextChanges) => {
    onChange(normalizeChordChanges(nextChanges, duration));
  }, [duration, onChange]);

  const selectBeat = (beat, change) => {
    setSelectedBeatIndex(beat.index);
    setSelectedChangeId(change?.id || null);
  };

  const addOrUpdateChange = (beat, chord = getChordLabel(activeChange || "C"), patch = {}) => {
    const existing = getChordChangeAtBeat(changes, beat);
    if (existing) {
      commit(changes.map((change) => change.id === existing.id ? { ...change, chord, ...patch } : change));
      setSelectedChangeId(existing.id);
      return;
    }
    const created = normalizeChordEvent({
      chord,
      start: beat.start,
      time: beat.start,
      section: activeChange?.section || "",
      timing_source: "manual",
      ...patch,
    }, changes.length, beat.start, beat.duration);
    commit([...changes, created]);
    setSelectedChangeId(created.id);
  };

  const updateChange = (change, patch) => {
    if (patch.focusSelector) {
      setFocusSelectorToken((token) => token + 1);
      return;
    }
    commit(changes.map((candidate) => candidate.id === change.id ? { ...candidate, ...patch } : candidate));
  };

  const removeChange = (targetChange = selectedChange) => {
    if (!targetChange) return;
    commit(changes.filter((change) => change.id !== targetChange.id));
    setSelectedChangeId(null);
  };

  const copyPrevious = () => {
    if (!selectedBeat) return;
    const prior = getActiveChordAtTime(changes, Math.max(0, selectedBeat.start - 0.03));
    if (prior) addOrUpdateChange(selectedBeat, getChordLabel(prior));
  };

  const moveChange = (direction) => {
    if (!selectedChange || !selectedBeat) return;
    const destinationIndex = Math.max(0, Math.min(beats.length - 1, selectedBeat.index + direction));
    const destination = beats[destinationIndex];
    commit(changes.map((change) => change.id === selectedChange.id
      ? { ...change, start: destination.start, time: destination.start }
      : change));
    setSelectedBeatIndex(destinationIndex);
    onSeek?.(destination.start);
  };

  const moveBeatSelection = (direction) => {
    const destinationIndex = Math.max(0, Math.min(beats.length - 1, selectedBeatIndex + direction));
    const destination = beats[destinationIndex];
    selectBeat(destination, getChordChangeAtBeat(changes, destination));
    onSeek?.(destination.start);
  };

  const splitSection = () => {
    if (!selectedBeat) return;
    addOrUpdateChange(
      selectedBeat,
      getChordLabel(selectedChange || activeChange || "C"),
      { section: selectedChange?.section || activeChange?.section || "Verse" }
    );
  };

  const previewFromCell = () => {
    if (!selectedBeat) return;
    onSeek?.(selectedBeat.start);
    if (!isPlaying) onTogglePlayback?.();
  };

  const handleKeyboard = (event) => {
    const typing = ["INPUT", "SELECT", "TEXTAREA"].includes(event.target.tagName);
    if (typing) return;
    if (event.key === " ") {
      event.preventDefault();
      onTogglePlayback?.();
    } else if (event.key.toLowerCase() === "a") {
      addOrUpdateChange(selectedBeat);
    } else if (event.key === "Delete") {
      removeChange();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveBeatSelection(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      moveBeatSelection(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveBeatSelection(-16);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveBeatSelection(16);
    } else if (event.key.toLowerCase() === "c") {
      copyPrevious();
    } else if (event.key.toLowerCase() === "p") {
      previewFromCell();
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!selectedChange) addOrUpdateChange(selectedBeat);
      setFocusSelectorToken((token) => token + 1);
    }
  };

  return (
    <section tabIndex={0} onKeyDown={handleKeyboard} className="space-y-5 rounded-3xl border border-white/10 bg-white/[0.045] p-4 outline-none focus:border-cyan-300/35 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200/65">Live Chord Editor</p>
          <h2 className="mt-1 text-xl font-semibold text-white">{viewMode === "rail" ? "Edit Rail" : "Chord overview"}</h2>
          <p className="text-sm text-slate-400">{viewMode === "rail" ? "Edita cambios en una sola linea mientras reproduce." : "Revisa frases y compases en el plano completo."} Una celda vacia sostiene el acorde anterior.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-cyan-300/15 bg-cyan-300/8 px-4 py-2 text-sm text-slate-300">
          <span>Acorde sostenido: <strong className="ml-2 text-xl text-cyan-100">{getChordLabel(activeChange)}</strong></span>
          <span>Siguiente: <strong className="ml-2 text-lg text-violet-200">{getChordLabel(nextChange)}</strong></span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => addOrUpdateChange(selectedBeat)} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-cyan-300 px-4 text-sm font-semibold text-slate-950"><Plus className="h-4 w-4" /> Agregar cambio aqui</button>
          <button type="button" onClick={copyPrevious} className="min-h-11 rounded-xl border border-white/10 px-4 text-sm text-white">Copiar acorde anterior</button>
          <button type="button" onClick={() => moveBeatSelection(-1)} disabled={selectedBeatIndex <= 0} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-white disabled:opacity-40" aria-label="Beat anterior"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" onClick={() => moveBeatSelection(1)} disabled={selectedBeatIndex >= beats.length - 1} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-white disabled:opacity-40" aria-label="Beat siguiente"><ChevronRight className="h-4 w-4" /></button>
          <button type="button" onClick={() => removeChange()} disabled={!selectedChange} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-red-300/20 px-4 text-sm text-red-100 disabled:opacity-40"><Trash2 className="h-4 w-4" /> Eliminar cambio</button>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-white/10 bg-black/20 p-1 text-slate-200">
          <button type="button" onClick={() => setViewMode("rail")} className={`rounded-lg px-3 py-2 text-xs font-semibold ${viewMode === "rail" ? "bg-cyan-300 text-slate-950" : "text-slate-300"}`}>Edit Rail</button>
          <button type="button" onClick={() => setViewMode("grid")} className={`rounded-lg px-3 py-2 text-xs font-semibold ${viewMode === "grid" ? "bg-violet-300 text-slate-950" : "text-slate-300"}`}>Chord overview</button>
          <button type="button" onClick={() => setZoom((current) => Math.max(0.7, current - 0.15))} className="grid h-9 w-9 place-items-center"><Minus className="h-4 w-4" /></button>
          <span className="min-w-14 text-center text-xs font-semibold">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((current) => Math.min(2.2, current + 0.15))} className="grid h-9 w-9 place-items-center"><Plus className="h-4 w-4" /></button>
        </div>
      </div>

      {viewMode === "rail" ? (
        <ChordBeatCarousel
          mode="edit"
          events={changes}
          bpm={bpm}
          duration={duration}
          currentTime={currentTime}
          selectedBeatIndex={selectedBeatIndex}
          selectedChangeId={selectedChange?.id || selectedChangeId}
          onSeek={onSeek}
          onSelectBeat={selectBeat}
          onAddChordChange={(beat) => addOrUpdateChange(beat)}
          onUpdateChordChange={updateChange}
          onRemoveChordChange={removeChange}
          snapToBeat
          autoScroll={isPlaying}
          zoom={zoom}
        />
      ) : (
        <ChordGridComposer
          mode="edit"
          events={changes}
          bpm={bpm}
          duration={duration}
          currentTime={currentTime}
          selectedCell={selectedBeat}
          selectedChangeId={selectedChange?.id || selectedChangeId}
          onSeek={onSeek}
          onSelectCell={selectBeat}
          onAddChordChange={(beat) => addOrUpdateChange(beat)}
          onUpdateChordChange={updateChange}
          onRemoveChordChange={removeChange}
          autoFollow={isPlaying}
          zoom={zoom}
        />
      )}

      <div className="grid gap-4 rounded-2xl border border-cyan-300/15 bg-black/20 p-4 xl:grid-cols-[1.6fr_repeat(3,minmax(0,1fr))]">
        <AdminChordSelect
          value={selectedChange || activeChange}
          songKey={musicalKey}
          recentChords={recentChords}
          previousChord={getActiveChordAtTime(changes, Math.max(0, (selectedBeat?.start || 0) - 0.03))}
          onUsePrevious={copyPrevious}
          focusToken={focusSelectorToken}
          onChange={(value) => {
            if (selectedChange) updateChange(selectedChange, { chord: value });
            else addOrUpdateChange(selectedBeat, value);
          }}
        />
        <div className="space-y-2 text-xs font-semibold uppercase text-slate-400">
          Beat seleccionado
          <p className="rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-sm font-normal text-white">
            Bar {(selectedBeat?.barIndex || 0) + 1} / Beat {selectedBeat?.beatInBar || 1}<br />
            <span className="text-slate-400">{Number(selectedBeat?.start || 0).toFixed(2)}s</span><br />
            <span className="text-slate-400">Sostenido: </span>{getChordLabel(getActiveChordAtTime(changes, selectedBeat?.start || 0))}<br />
            <span className="text-slate-400">Cambio aqui: </span>{selectedChange ? getChordLabel(selectedChange) : "--"}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => moveChange(-1)} disabled={!selectedChange || selectedBeatIndex <= 0} className="rounded-lg border border-white/10 px-3 py-2 text-white disabled:opacity-35">Mover izq.</button>
            <button type="button" onClick={() => moveChange(1)} disabled={!selectedChange || selectedBeatIndex >= beats.length - 1} className="rounded-lg border border-white/10 px-3 py-2 text-white disabled:opacity-35">Mover der.</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={splitSection} className="rounded-lg border border-white/10 px-3 py-2 text-white">Dividir seccion</button>
            <button type="button" onClick={previewFromCell} className="rounded-lg border border-cyan-300/20 px-3 py-2 text-cyan-100">Preview aqui</button>
          </div>
        </div>
        <label className="text-xs font-semibold uppercase text-slate-400">Seccion
          <select value={selectedChange?.section || ""} disabled={!selectedChange} onChange={(event) => updateChange(selectedChange, { section: event.target.value })} className="mt-2 min-h-11 w-full rounded-xl border border-white/10 bg-slate-950 px-3 text-sm text-white disabled:opacity-40">
            <option value="">Sin seccion</option>
            {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold uppercase text-slate-400">Timing source
          <select value={selectedChange?.timing_source || "manual"} disabled={!selectedChange} onChange={(event) => updateChange(selectedChange, { timing_source: event.target.value })} className="mt-2 min-h-11 w-full rounded-xl border border-white/10 bg-slate-950 px-3 text-sm text-white disabled:opacity-40">
            {TIMING_OPTIONS.map((timing) => <option key={timing} value={timing}>{timing}</option>)}
          </select>
          <p className="mt-3 text-[11px] normal-case text-slate-500">Space play/pause | A agregar | Delete borrar | Flechas navegar | C copiar | P preview | Enter acorde</p>
        </label>
      </div>
    </section>
  );
};

export default AdminChordTimelineEditor;
